import React from 'react';
import PageHero from '../components/PageHero';
import SourceLink from '../components/SourceLink';
import { SOURCES } from '../content/site';

const Methods: React.FC = () => (
  <div>
    <PageHero
      title="Data & Methods"
      lead="Everything on this site is built from public data with methods that are described, coded and versioned. This page lists the sources, the modelling approach and the principles that every result follows."
    />

    <section className="bg-white section-padding">
      <div className="container-width">
        <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-8">Public data sources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Src name="EIA-930 Hourly Electric Grid Monitor" text="Hourly demand, day-ahead demand forecast, net generation and interchange for ~65 balancing authorities, since 2015." link={SOURCES.eia930} />
          <Src name="ERCOT" text="Hourly load by weather zone; historical public downloads." />
          <Src name="PJM Data Miner 2" text="Zonal hourly load and forecasts; free registration." />
          <Src name="MISO, SPP, CAISO OASIS, NYISO, ISO-NE" text="Public hourly load and, where available, forecast archives." />
          <Src name="NOAA ISD / GHCN" text="Station weather for temperature-driven regimes and extreme-day definitions." />
          <Src name="FERC, NERC, DOE" text="Event inquiries, reliability assessments and policy documents used for context and event calendars." />
        </div>
      </div>
    </section>

    <section className="bg-gray-50 section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">Forecasting</h2>
            <ul className="space-y-3 text-gray-700">
              <li>• Ensembles of gradient-boosted trees and neural sequence models, with calibrated prediction intervals.</li>
              <li>• Hierarchical reconciliation across zones and the system total.</li>
              <li>• Regime-aware models for cold snaps and heat waves, trained explicitly on extreme events.</li>
              <li>• Large-load tracking that treats data-center and industrial additions as structural interventions, not noise.</li>
              <li>• Fuzzy and probabilistic components for uncertainty quantification.</li>
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">Data quality</h2>
            <ul className="space-y-3 text-gray-700">
              <li>• Recursive typicality-and-eccentricity analysis (TEDA) for streaming anomaly scores.</li>
              <li>• Sparse autoencoders for detection and correction of outliers in demand curves. <SourceLink source={SOURCES.energies2024} /></li>
              <li>• Extreme-preserving rules cross-checked with weather and neighbouring series.</li>
              <li>• Audit trail of every correction, with reason codes.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white section-padding">
      <div className="container-width">
        <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-8">Principles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Principle title="Public data only" text="No proprietary or employer data is used, ever." />
          <Principle title="Reproducible" text="Every chart and table ships with the notebook that produced it and a pinned data snapshot." />
          <Principle title="Honest about limits" text="Results are reported with their limitations, including when a method underperforms a baseline." />
          <Principle title="Versioned and citable" text="Releases, reports and notes carry DOIs; corrections are logged, not overwritten." />
        </div>
      </div>
    </section>
  </div>
);

const Src: React.FC<{ name: string; text: string; link?: typeof SOURCES[keyof typeof SOURCES] }> = ({ name, text, link }) => (
  <div className="bg-gray-50 rounded-xl p-6 shadow">
    <h3 className="font-poppins font-semibold text-dark-gray mb-2">{name}</h3>
    <p className="text-gray-600 text-sm mb-2">{text}</p>
    {link && <SourceLink source={link} />}
  </div>
);

const Principle: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <div className="bg-gray-50 rounded-xl p-6 text-center shadow">
    <h3 className="font-montserrat font-semibold text-primary-red mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{text}</p>
  </div>
);

export default Methods;
